import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useI18n } from "../../../hooks/useI18n";
import DateRangePicker from "../../../components/inputs/daterangepicker";
import { thunkFetchAverageReceiptStatistics } from "../../../store/statistics/averagereceipt/actions";
import { AppState } from "../../../store";
import { ReceiptsStatisticsState } from "../../../store/statistics/averagereceipt/types";
import moment from "moment";
import { Receipt } from "../../../models/Statistics";
import { Grid, Hidden, Tab, useMediaQuery } from "@material-ui/core";
import { getDates } from "../shared/graphFunctions";
import LineChart from "../components/LineChart";
import { LineChartData } from "../components/LineChart/types";
import TableWrapper from "../components/TableWrapper";
import BarChart from "../components/BarChart";
import { useTheme } from "@material-ui/core/styles";
import { UsersStatisticsState } from "../../../store/statistics/user/types";
import { thunkFetchUserStatistics } from "../../../store/statistics/user/actions";
import { PieChartData } from "../components/PieChart/types";
import PieChart from "../components/PieChart";
import SalesByAgeGroup from "./SalesByAgeGroup";
import StatTable from "../components/StatTable";
import { ProductSalesStatistics } from "./ProductSalesStatistics";
import { Alert, TabContext, TabList, TabPanel } from "@material-ui/lab";
import { FilteredStoresState } from "../../../store/stores/types";
import { thunkFetchFilteredStores } from "../../../store/stores/actions";
import { useLoadIfEmpty } from "../../../hooks/useLoadIfEmpty";
import { Currency } from "../../../components/misc/Currency";
import { ExclVat } from "../components/exclVat";
import { Authenticated, hasRole } from "../../../models/User";
import { UserContext } from "../../../UserProvider";
import AllSalesStatistics from "./AllSalesStatistics";

export const SalesStatistics: FunctionComponent = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const currentUser = useContext(UserContext) as Authenticated;
  const { t } = useI18n();

  const [dateRange, setDateRange] = useState<[Date, Date]>([
    moment().toDate(),
    moment().toDate()
  ]);

  const userStatisticsState = useSelector<AppState, UsersStatisticsState>(
    s => s.userStatistics
  );

  const salesStatisticsState = useSelector<AppState, ReceiptsStatisticsState>(
    s => s.averageReceiptStatistics
  );

  const filteredStoresState = useSelector<AppState, FilteredStoresState>(
    s => s.filteredStores
  );

  useLoadIfEmpty(filteredStoresState.isLoading, thunkFetchFilteredStores);

  let { receiptStatistics, isLoading } = salesStatisticsState;
  const [selectedTab, setSelectedTab] = useState("1");
  const [productSalesState, setProductSalesState] = useState({} as any);

  useEffect(() => {
    if (dateRange) {
      dispatch(
        thunkFetchAverageReceiptStatistics(
          dateRange[0],
          dateRange[1],
          filteredStoresState.isLoading
            ? []
            : filteredStoresState.stores.map(s => s.id)
        )
      );
      dispatch(thunkFetchUserStatistics(dateRange[0], dateRange[1]));
      setProductSalesState({} as any);
    }
  }, [dateRange, filteredStoresState]);

  const populateGrossSalesGraphData = (salesPerDay: Map<Date, Receipt>) => {
    let data = [];
    let dates = getDates(dateRange[0], dateRange[1]);

    for (let i = 0; i < dates.length; i++) {
      let formatted = moment(dates[i]).format("YYYY-MM-DDT00:00:00");
      if (formatted in salesPerDay) {
        // @ts-ignore
        data.push(salesPerDay[formatted].sum);
      } else {
        data.push(0);
      }
    }

    return data;
  };

  const getTargetSum = () => {
    return {
      target1: process.env.REACT_APP_STORE_TARGET_SMALL,
      target2: process.env.REACT_APP_STORE_TARGET_LARGE
    };
  };

  const populateGrossSalesDuringPeriodChartData = (): LineChartData => {
    return {
      labels: getDates(dateRange[0], dateRange[1]).map(s =>
        moment(s).format("YYYY-MM-DD")
      ),
      datasets: receiptStatistics.map(s => {
        return {
          label: s.storeName,
          data: populateGrossSalesGraphData(s.sumReceiptEachDay)
        };
      })
    };
  };

  const groupSalesByWeekday = (): PieChartData => {
    const salesByWeekday = receiptStatistics.reduce((accStore: any, curr) => {
      let daysWithSales = Object.keys(curr.sumReceiptEachDay).reduce(
        (accEachDay: any, dateKey) => {
          let dayOfWeek = moment(dateKey).format("dddd");
          if (!accEachDay[dayOfWeek]) {
            accEachDay[dayOfWeek] = 0;
          }
          //@ts-ignore
          accEachDay[dayOfWeek] += curr.sumReceiptEachDay[dateKey].sum;
          return accEachDay;
        },
        {}
      );

      return { ...accStore, ...daysWithSales };
    }, {});

    return {
      labels: Object.keys(salesByWeekday).map(a => a),
      datasets: [
        {
          label: "",
          //@ts-ignore
          data: Object.values(salesByWeekday).map(a => a),
          rgbColor: Object.keys(salesByWeekday).map(a => ({
            r: Math.floor(Math.random() * 255),
            g: Math.floor(Math.random() * 255),
            b: Math.floor(Math.random() * 255)
          }))
        }
      ]
    };
  };

  const populateAgeGroupSalesByGender = () => {
    return userStatisticsState.userStatistics.reduce((acc: any, curr) => {
      if (!acc[curr.age]) {
        acc[curr.age] = { salesMen: 0, salesWomen: 0 };
      }

      acc[curr.age].salesWomen += curr.gender === 0 ? curr.sales : 0;
      acc[curr.age].salesMen += curr.gender === 1 ? curr.sales : 0;
      return acc;
    }, {});
  };

  const ageGroupSalesByGender = populateAgeGroupSalesByGender();

  const populateAgeGroupPie = (): PieChartData => {
    return {
      labels: Object.keys(ageGroupSalesByGender).map(
        a => `${a} < ${Number(a) + 9}`
      ),
      datasets: [
        {
          label: "",
          data: Object.keys(ageGroupSalesByGender).map(
            a =>
              ageGroupSalesByGender[a].salesWomen +
              ageGroupSalesByGender[a].salesMen
          ),
          rgbColor: Object.keys(ageGroupSalesByGender).map(a => ({
            r: Math.floor(Math.random() * 255),
            g: Math.floor(Math.random() * 255),
            b: Math.floor(Math.random() * 255)
          }))
        }
      ]
    };
  };

  const populateGenderGroupPie = (): PieChartData => {
    let gender = { men: 0, women: 0 };
    for (const ageGroup of Object.values(ageGroupSalesByGender)) {
      // @ts-ignore
      gender.men += ageGroup.salesMen;
      // @ts-ignore
      gender.women += ageGroup.salesWomen;
    }

    return {
      labels: ["Women", "Men"],
      datasets: [
        {
          label: "",
          data: [gender.women, gender.men],
          rgbColor: [
            { r: 58, g: 160, b: 75 },
            { r: 5, g: 89, b: 141 }
          ]
        }
      ]
    };
  };

  const populateAgeChart = () => {
    const stores = [
      ...new Set(userStatisticsState.userStatistics.map(u => u.storeName))
    ];

    const ageGroups = userStatisticsState.userStatistics.reduce(
      (acc: any, curr) => {
        if (!acc[curr.age]) {
          acc[curr.age] = Array(stores.length).fill(0);
        }
        acc[curr.age][stores.indexOf(curr.storeName)] += curr.sales;
        return acc;
      },
      {}
    );

    let datasets = [];

    for (const [key, value] of Object.entries(ageGroups)) {
      datasets.push({
        label: `${key} < ${Number(key) + 9}`,
        rgbColor: {
          r: Math.floor(Math.random() * 255),
          g: Math.floor(Math.random() * 255),
          b: Math.floor(Math.random() * 255)
        },
        data: value,
        stack: "0"
      });
    }

    return datasets;
  };

  const handleChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setSelectedTab(newValue);
  };

  return (
    <>
      <DateRangePicker
        disabled={isLoading}
        onChange={setDateRange}
        value={dateRange}
      />
      <ExclVat />
      <Grid
        container
        direction={"row"}
        justifyContent={"space-evenly"}
        alignItems={"flex-start"}
        style={{ marginTop: "2rem" }}
        spacing={useMediaQuery(theme.breakpoints.down("xs")) ? 0 : 3}
      >
        <Grid item xs={12} lg={12}>
          <TableWrapper
            title={
              selectedTab === "1"
                ? t("common.tableHeaders.grossSales")
                : t("common.tableHeaders.productSales")
            }
          >
            <TabContext value={selectedTab}>
              <TabList
                onChange={handleChange}
                style={{ borderBottom: "1px solid #e0e0e0" }}
              >
                <Tab label={t("common.tableHeaders.grossSales")} value="1" />
                <Tab label={t("common.tableHeaders.productSales")} value="2" />
                {hasRole(currentUser, ["Root"]) ? (
                  <Tab label={"All tenant Gross sales"} value="3" />
                ) : (
                  <></>
                )}
              </TabList>
              <TabPanel value="1" style={{ padding: 0 }}>
                <Hidden xsDown>
                  <Alert severity={"info"}>
                    Actual sales after campaign discounts
                  </Alert>
                  <StatTable
                    isLoading={isLoading}
                    exportName={"Gross sale statistics"}
                    columns={[
                      {
                        title: "Store",
                        field: "storeName"
                      },
                      {
                        title: "Sales / period",
                        field: "periodsum",
                        render: (rowData: any) => (
                          <Currency value={rowData.periodsum} />
                        )
                      },
                      {
                        title: "Purchases / period",
                        field: "periodcount"
                      },
                      {
                        title: "Unique users / period",
                        field: "uniqueUsers"
                      },
                      {
                        title: "New users / period",
                        field: "newUsers"
                      },
                      {
                        title: "Sales / Day",
                        field: "dailysum",
                        cellStyle: { borderLeft: "1px solid #e0e0e0" },
                        render: (rowData: any) => (
                          <Currency value={rowData.dailysum} />
                        )
                      },
                      {
                        title: "Purchases / Day",
                        field: "dailycount"
                      }
                    ]}
                    data={
                      !isLoading
                        ? receiptStatistics.map(r => ({
                            storeName: r.storeName,
                            periodsum: r.period.sum,
                            periodcount: r.period.count,
                            uniqueUsers: r.uniqueUsers,
                            newUsers: r.newUsers,
                            dailysum: r.daily.sum,
                            dailycount: r.daily.count.toFixed(2)
                          }))
                        : []
                    }
                    detailPanel={(rowData: any) => (
                      <SalesByAgeGroup
                        storeName={rowData.storeName}
                        userStatistics={userStatisticsState.userStatistics}
                        periodFrom={dateRange[0]}
                        periodTo={dateRange[1]}
                        isLoading={userStatisticsState.isLoading}
                      />
                    )}
                  />
                </Hidden>
                <Hidden smUp>
                  <StatTable
                    isLoading={isLoading}
                    columns={[
                      {
                        title: "Store",
                        field: "storeName"
                      },
                      {
                        title: "Sales / period",
                        field: "periodsum",
                        render: (rowData: any) => (
                          <Currency value={rowData.periodsum} />
                        )
                      },
                      {
                        title: "Purchases / period",
                        field: "periodcount"
                      }
                    ]}
                    data={
                      !isLoading
                        ? receiptStatistics.map(r => ({
                            storeName: r.storeName,
                            periodsum: r.period.sum,
                            periodcount: r.period.count
                          }))
                        : []
                    }
                  />
                </Hidden>
              </TabPanel>
              <TabPanel value="2" style={{ padding: 0 }}>
                <ProductSalesStatistics
                  dateRange={dateRange}
                  storeIds={filteredStoresState.stores.map(s => s.id)}
                  onLoad={(columns, data) =>
                    setProductSalesState({ columns, data })
                  }
                  productSales={productSalesState}
                />
              </TabPanel>
              <TabPanel value="3" style={{ padding: 0 }}>
                <AllSalesStatistics dateRange={dateRange} />
              </TabPanel>
            </TabContext>
          </TableWrapper>
        </Grid>
        <Grid item xs={12} lg={6}>
          <BarChart
            title={"Daily sales by store"}
            chartData={{
              labels: receiptStatistics.map(r => r.storeName),
              datasets: [
                {
                  label: "",
                  data: receiptStatistics.map(r => r.daily.sum)
                }
              ]
            }}
            showLegend={false}
            isLoading={isLoading}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <BarChart
            title={"Average purchase by store"}
            chartData={{
              labels: receiptStatistics.map(r => r.storeName),
              datasets: [
                {
                  label: "",
                  data: receiptStatistics.map(r => r.daily.sum / r.daily.count)
                }
              ]
            }}
            showLegend={false}
            isLoading={isLoading}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <PieChart
            title={"Sales by age group"}
            polar={true}
            isLoading={userStatisticsState.isLoading}
            chartData={populateAgeGroupPie()}
          />
        </Grid>
        <Grid item container xs={12} md={6} spacing={3}>
          <Grid item xs={12}>
            <BarChart
              title={"Sales by age group and gender"}
              chartData={{
                labels: Object.keys(ageGroupSalesByGender).map(
                  a => `${a} < ${Number(a) + 9}`
                ),
                datasets: [
                  {
                    label: "Men",
                    data: Object.keys(ageGroupSalesByGender).map(
                      a => ageGroupSalesByGender[a].salesMen
                    ),
                    rgbColor: { r: 5, g: 89, b: 141 }
                  },
                  {
                    label: "Women",
                    data: Object.keys(ageGroupSalesByGender).map(
                      a => ageGroupSalesByGender[a].salesWomen
                    ),
                    rgbColor: { r: 58, g: 160, b: 75 }
                  }
                ]
              }}
              showLegend={true}
              isLoading={userStatisticsState.isLoading}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PieChart
              title={"Sales by gender"}
              isLoading={userStatisticsState.isLoading}
              chartData={populateGenderGroupPie()}
              options={{
                plugins: {
                  legend: {
                    display: false
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <PieChart
              title={"Sales by day"}
              isLoading={isLoading}
              chartData={groupSalesByWeekday()}
              polar={true}
              options={{
                plugins: {
                  legend: {
                    display: false
                  }
                }
              }}
            />
          </Grid>
        </Grid>
        <Hidden xsDown>
          <Grid item xs={12}>
            <BarChart
              title={"Sales by store and age group"}
              chartData={{
                labels: [
                  ...new Set(
                    userStatisticsState.userStatistics.map(u => u.storeName)
                  )
                ],
                //@ts-ignore
                datasets: populateAgeChart()
              }}
              options={{
                scales: {
                  x: {
                    stacked: true
                  },
                  y: {
                    stacked: true
                  }
                }
              }}
              showLegend={true}
              isLoading={isLoading}
            />
          </Grid>
          <Grid item xs={12}>
            <LineChart
              title={"Gross sales excl. vat"}
              isLoading={isLoading}
              targetLines={[
                {
                  title: "Target (S)",
                  value: parseInt(getTargetSum().target1 ?? ""),
                  rgbColor: { r: 85, g: 52, b: 235 }
                },
                {
                  title: "Target (L)",
                  value: parseInt(getTargetSum().target2 ?? ""),
                  rgbColor: { r: 75, g: 192, b: 192 }
                }
              ]}
              chartData={populateGrossSalesDuringPeriodChartData()}
            />
          </Grid>
        </Hidden>
      </Grid>
    </>
  );
};
